import {
  Accordion,
  AccordionHeader,
  AccordionBody,
} from "@material-tailwind/react";
import { useCallback, useState } from "react";
import { useWorkorderContext } from "../../../../../context/WorkorderContext";
import { useWorkorderMachine } from "../../../../../hooks/useWorkorderMachine";
import { useWorkorderHose } from "../../../../../hooks/hoses/useWorkorderHoses";
import { useDialogs } from "../../../../../hooks/useDialogs";
import { useMachineEditState } from "../../../../../hooks/machines/useMachineEditState";
import { t } from "i18next";
import toast from "react-hot-toast";

import MachineHeader from "../../../../../components/workorders/machines/MachineHeader";
import MachineNotes from "../../../../../components/workorders/machines/MachineNotes";
import MachineContent from "../../../../../components/workorders/machines/MachineContent";
import MachineDeleteDialog from "../../../../../components/workorders/machines/MachineDeleteDialog";
import HoseDraftRow from "../../../../../components/workorders/hoses/HoseDraftRow";

/**
 * Props pour le composant MachineSection
 */
interface MachineSectionProps {
  machinesCustomer: EasyTech.Machine[];
  workorderId: string;
  woMachineId: string;
  canEdit: boolean;
  notes: string;
  onMachineUpdated?: (
    machineId: string,
    newMachine: EasyTech.Machine,
    woMachineId: string,
    notes: string
  ) => void;
  onMachineRemoved?: (woMachineId: string) => void;
}

/**
 * Composant qui affiche et gère les informations d'une machine dans un workorder
 * Permet de visualiser, éditer et supprimer la machine ainsi que ses pièces et flexibles associés
 */
export default function MachineSection({
  machinesCustomer,
  workorderId,
  woMachineId,
  canEdit,
  notes: initialNotes,
  onMachineUpdated,
  onMachineRemoved,
}: MachineSectionProps) {
  // État de l'accordéon
  const [openAccordion, setOpenAccordion] = useState(true);
  
  // État pour l'ajout inline de hose
  const [showHoseDraft, setShowHoseDraft] = useState(false);

  // Hooks de contexte et API
  const { getMachines, setCurrentEditContext, setInlineAdd } = useWorkorderContext();
  const { updateMachine, removeMachine } = useWorkorderMachine(workorderId);
  const { addHose, removeHose } = useWorkorderHose(workorderId);
  const { dialogs } = useDialogs(["part", "confirmDelete"]);

  // Récupérer la machine workorder
  const machines = getMachines() || [];
  const workorderMachine = machines.find((wm) => wm.id === woMachineId);

  // État d'édition de la machine via le hook personnalisé
  const editState = useMachineEditState({
    initialMachineId: workorderMachine?.machine?.id || "",
    initialNotes: initialNotes
  });

  // Handler pour l'affichage du formulaire d'ajout de hose
  const handleOpenHoseForm = useCallback(() => {
    setShowHoseDraft(true);
  }, []);

  // Handler pour l'annulation de l'ajout de hose
  const handleCancelAddHose = useCallback(() => {
    setShowHoseDraft(false);
  }, []);

  // Handler pour la sauvegarde d'un nouveau hose
  const handleSaveHose = useCallback(
    async (hoseData: { name: string; description?: string; qrCode: string }) => {
      try {
        const newHoseData = {
          name: hoseData.name,
          description: hoseData.description,
          qrCode: hoseData.qrCode,
          workOrder: { id: workorderId },
          machine: { id: woMachineId }
        };

        const newHose = await addHose(newHoseData);
        toast.success(t("workorder:hoses:success:hoseAdded"));
        setShowHoseDraft(false);

        // Si le hose est créé avec succès, ouvrir le part draft pour ajouter des parts 
        if (newHose && typeof newHose === "object" && "id" in newHose) {
          setCurrentEditContext(`hose-${newHose.id}`);
          setInlineAdd(true);
        }
      } catch (err) {
        console.error("Erreur lors de l'ajout du flexible dans la machine", err);
        toast.error(t("workorder:hoses:error:hoseAddError"));
      }
    },
    [addHose, workorderId, woMachineId, setCurrentEditContext, setInlineAdd]
  );

  const handleRemoveHose = useCallback(
    async (hoseId: string) => {
      try {
        await removeHose(hoseId);
      } catch (err) {
        console.error("workorder:hoses:error:hoseRemoveError", err);
      }
    },
    [removeHose]
  );

  // Handler pour l'ouverture du part draft pour ajouter des parts
  const handleOpenPartDialog = useCallback(() => {
    // Définir le contexte d'édition pour cette machine spécifique
    setCurrentEditContext(`machine-${woMachineId}`);
    setInlineAdd(true);
  }, [woMachineId, setCurrentEditContext, setInlineAdd]);

  // Si la machine n'existe pas dans le workorder, ne pas rendre le composant
  if (!workorderMachine) {
    console.warn(t("workorder:machines:error:machineNotFound"));
    return null;
  }

  // Gestionnaire pour la sauvegarde des modifications
  const handleSaveClick = async () => {
    // Si aucune modification n'a été faite, simplement sortir du mode édition
    if (
      editState.selectedMachineId === workorderMachine.machine.id &&
      editState.machineNotes === (workorderMachine?.notes || "")
    ) {
      editState.setIsEditing(false);
      return;
    }

    try {
      // Trouver la nouvelle machine sélectionnée
      const selectedMachine = machinesCustomer.find(
        (m) => m.id === editState.selectedMachineId
      );
      if (!selectedMachine) {
        console.error(
          t("workorder:machines:error:machineNotFound"),
          editState.selectedMachineId
        );
        editState.setIsEditing(false);
        return;
      }

      // Préparer les données pour la mise à jour
      const updateData = {
        machine: { id: selectedMachine.id },
        notes: editState.machineNotes,
      };

      // Appeler l'API pour mettre à jour la machine
      const updatedWoMachine = await updateMachine(woMachineId, updateData);

      // Informer le parent de la mise à jour
      if (onMachineUpdated && updatedWoMachine) {
        onMachineUpdated(
          selectedMachine.id,
          selectedMachine,
          woMachineId,
          editState.machineNotes
        );
      }

      // Forcer la mise à jour de notre référence locale à la machine
      // Cela garantit que l'affichage reflète la nouvelle machine
      if (workorderMachine) {
        workorderMachine.machine = selectedMachine;
        workorderMachine.notes = editState.machineNotes;
      }

      // Sortir du mode édition
      editState.setIsEditing(false);
    } catch (err) {
      console.error(t("workorder:machines:error:machineUpdateError"), err);
      
      if (err instanceof Error && err.message === "Error this machine is already in the workorder") {
        toast.error(t("workorder:machines:error:machineAlreadyInWorkorder"));
      } else {
        toast.error(t("workorder:machines:error:machineUpdateError"));
      }
    }
  };

  // Gestionnaire pour la suppression de la machine
  const handleConfirmDelete = async (): Promise<void> => {
    try {
      await removeMachine(woMachineId);

      if (onMachineRemoved) {
        onMachineRemoved(woMachineId);
      }
    } catch (error) {
      console.error(t("workorder:machines:error:machineRemoveError"), error);
    } finally {
      dialogs.confirmDelete.close();
    }
  };

  return (
    <div>
      <Accordion open={openAccordion} className="">
        <AccordionHeader className="[&>span]:hidden">
          <MachineHeader 
            isEditing={editState.isEditing}
            openAccordion={openAccordion}
            setOpenAccordion={setOpenAccordion}
            workorderMachine={workorderMachine}
            machinesCustomer={machinesCustomer}
            selectedMachineId={editState.selectedMachineId}
            setSelectedMachineId={editState.setSelectedMachineId}
            onEditClick={() => editState.setIsEditing(true)}
            onSaveClick={handleSaveClick}
            onCancelEdit={editState.resetEditState}
            onDeleteClick={() => dialogs.confirmDelete.open()}
            onAddHose={handleOpenHoseForm}
            onAddPart={handleOpenPartDialog}
            canEdit={canEdit}
          />
        </AccordionHeader>
        <AccordionBody className="px-0 py-0">
          {editState.isEditing && (
            <MachineNotes 
              notes={editState.machineNotes} 
              onChange={editState.setMachineNotes} 
            />
          )}

          {/* Afficher le formulaire d'ajout de hose si nécessaire */}
          {showHoseDraft && (
            <div className="px-4 py-4">
              <HoseDraftRow
                onSave={handleSaveHose}
                onCancel={handleCancelAddHose}
              />
            </div>
          )}

          <MachineContent 
            workorderId={workorderId}
            machineId={woMachineId}
            onRemoveHose={handleRemoveHose}
            onAddPart={handleOpenPartDialog}
            canEdit={canEdit}
          />
        </AccordionBody>
      </Accordion>

      {/* Dialogues */}
      <MachineDeleteDialog
        open={dialogs.confirmDelete.isOpen}
        onClose={() => dialogs.confirmDelete.close()}
        onConfirm={handleConfirmDelete}
      />
    </div>
  );
}
